import React from 'react';
import { FileTextIcon, MessageSquareIcon, CalendarIcon, ArrowRightIcon, CheckCircleIcon } from 'lucide-react';
export function ApplicationProcess() {
  const steps = [{
    icon: FileTextIcon,
    title: 'Apply in 5 Minutes',
    description: 'Short form about you, your role, your company, and AI goals. No essays, no credit card required.',
    color: 'from-cyan-500 to-blue-500',
    time: '5 min'
  }, {
    icon: MessageSquareIcon,
    title: '15-Minute Interview',
    description: 'Quick fit conversation with our team to align on outcomes and timing. We typically respond within 24 hours.',
    color: 'from-purple-500 to-pink-500',
    time: '15 min'
  }, {
    icon: CalendarIcon,
    title: 'Cohort Match',
    description: "If selected, you'll be assigned to the next available Cancun cohort. Confirmation within 48 hours.",
    color: 'from-green-500 to-emerald-500',
    time: '48 hrs'
  }];
  return <section className="py-24" data-id="element-1">
      <div className="max-w-7xl mx-auto px-6" data-id="element-2">
        <div className="text-center mb-16" data-id="element-3">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4" data-id="element-4">
            Simple, Fast Application Process
          </h2>
          <p className="text-xl text-slate-400 max-w-3xl mx-auto" data-id="element-5">
            From application to confirmation in 72 hours. Interview-only to
            ensure cohort quality.
          </p>
        </div>

        {/* Process steps */}
        <div className="grid md:grid-cols-3 gap-8 mb-16" data-id="element-6">
          {steps.map((step, index) => {
          const Icon = step.icon;
          return <div key={index} className="relative" data-id="element-7">
                <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-8 h-full hover:border-slate-700 transition-all duration-300" data-id="element-8">
                  <div className="flex items-center justify-between mb-6" data-id="element-9">
                    <div className={`w-16 h-16 bg-gradient-to-br ${step.color} rounded-xl flex items-center justify-center`} data-id="element-10">
                      <Icon className="w-8 h-8 text-white" data-id="element-11" />
                    </div>
                    <div className="text-2xl font-bold text-slate-600" data-id="element-12">
                      {step.time}
                    </div>
                  </div>

                  <div className="text-sm font-semibold text-slate-500 mb-2" data-id="element-13">
                    STEP {index + 1}
                  </div>

                  <h3 className="text-2xl font-bold text-white mb-4" data-id="element-14">
                    {step.title}
                  </h3>

                  <p className="text-slate-300" data-id="element-15">{step.description}</p>
                </div>

                {index < steps.length - 1 && <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2" data-id="element-16">
                    <ArrowRightIcon className="w-8 h-8 text-slate-700" data-id="element-17" />
                  </div>}
              </div>;
        })}
        </div>

        {/* Selection criteria */}
        <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 border border-slate-700 rounded-2xl p-12 mb-12" data-id="element-18">
          <h3 className="text-2xl font-bold text-white mb-6 text-center" data-id="element-19">
            Why Interview-Only Selection?
          </h3>
          <p className="text-xl text-slate-300 text-center max-w-3xl mx-auto mb-8" data-id="element-20">
            We restrict each cohort to leaders and teams who are serious about
            implementing what they learn. This ensures high-quality peer
            learning and actionable outcomes.
          </p>
          <div className="grid md:grid-cols-3 gap-6 text-center" data-id="element-21">
            <div data-id="element-22">
              <div className="text-3xl font-bold text-cyan-400 mb-2" data-id="element-23">
                Quality
              </div>
              <p className="text-slate-400" data-id="element-24">
                Ensure cohort fit and maximize peer learning value
              </p>
            </div>
            <div data-id="element-25">
              <div className="text-3xl font-bold text-purple-400 mb-2" data-id="element-26">
                Commitment
              </div>
              <p className="text-slate-400" data-id="element-27">
                Verify readiness to implement and take action
              </p>
            </div>
            <div data-id="element-28">
              <div className="text-3xl font-bold text-green-400 mb-2" data-id="element-29">
                Outcomes
              </div>
              <p className="text-slate-400" data-id="element-30">
                Match timing and goals to maximize impact
              </p>
            </div>
          </div>
        </div>

        {/* Friction reducers */}
        <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-8 mb-12" data-id="element-31">
          <h4 className="text-lg font-semibold text-white mb-6 text-center" data-id="element-32">
            What You Need to Know
          </h4>
          <div className="grid md:grid-cols-2 gap-6" data-id="element-33">
            <div className="flex items-start gap-3" data-id="element-34">
              <CheckCircleIcon className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" data-id="element-35" />
              <div data-id="element-36">
                <div className="font-semibold text-white mb-1" data-id="element-37">
                  No Credit Card Required
                </div>
                <p className="text-sm text-slate-400" data-id="element-38">
                  Apply and interview first. Payment only after acceptance.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3" data-id="element-39">
              <CheckCircleIcon className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" data-id="element-40" />
              <div data-id="element-41">
                <div className="font-semibold text-white mb-1" data-id="element-42">
                  Fast Response Time
                </div>
                <p className="text-sm text-slate-400" data-id="element-43">
                  We respond to applications within 24 hours, interviews
                  scheduled within 48.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3" data-id="element-44">
              <CheckCircleIcon className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" data-id="element-45" />
              <div data-id="element-46">
                <div className="font-semibold text-white mb-1" data-id="element-47">
                  Flexible Scheduling
                </div>
                <p className="text-sm text-slate-400" data-id="element-48">
                  Can't make the next cohort? We'll hold your spot for a future
                  date.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3" data-id="element-49">
              <CheckCircleIcon className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" data-id="element-50" />
              <div data-id="element-51">
                <div className="font-semibold text-white mb-1" data-id="element-52">
                  Questions? Talk to Us
                </div>
                <p className="text-sm text-slate-400" data-id="element-53">
                  Email us at hello@destameet.ai or schedule a quick call.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-54">
          <button className="group px-8 py-4 bg-gradient-to-r from-cyan-500 to-purple-500 text-white font-semibold rounded-lg hover:shadow-2xl hover:shadow-cyan-500/50 transition-all duration-300 flex items-center justify-center gap-2" data-id="element-55">
            Apply Now - 8 Spots Left for March
            <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-56" />
          </button>
          <button className="px-8 py-4 bg-slate-800/50 text-white font-semibold rounded-lg border border-slate-700 hover:bg-slate-800 transition-all duration-300" data-id="element-57">
            Schedule a Discovery Call
          </button>
        </div>
      </div>
    </section>;
}